import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, addDoc, collection, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useConfig } from '../contexts/ConfigContext';
import LoadingAnimation from './LoadingAnimation';
import ReactQuill from 'react-quill-new';

// Must be defined at module scope — defining inside the component causes
// Quill to remount on every render and lose cursor focus.
const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic'],
    [{ header: 2 }, { header: 3 }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean']
  ]
};

const QUILL_FORMATS = ['bold', 'italic', 'header', 'list'];

const AdminPrayerEditor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const config = useConfig();
  const isEditing = Boolean(id);

  const [authLoading, setAuthLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(isEditing);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [categories, setCategories] = useState([]);
  const [categoryInput, setCategoryInput] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/login', { state: { from: location.pathname } });
        return;
      }
      if (config.configLoaded && config.adminEmails.length > 0 && !config.adminEmails.includes(user.email)) {
        navigate('/');
        return;
      }
      if (config.configLoaded) {
        setAuthLoading(false);
        if (isEditing) {
          await loadPrayer();
          setDataLoading(false);
        }
      }
    });
    return () => unsubscribe();
  }, [config.configLoaded, config.adminEmails]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPrayer = async () => {
    const snap = await getDoc(doc(db, 'prayers', id));
    if (!snap.exists()) { navigate('/admin/prayers'); return; }
    const data = snap.data();
    setName(data.name || '');
    setDescription(data.description || '');
    setCategories(data.categories || []);
    setContent(data.content || '');
  };

  const addCategory = () => {
    const tag = categoryInput.trim();
    if (!tag || categories.includes(tag)) { setCategoryInput(''); return; }
    setCategories(prev => [...prev, tag]);
    setCategoryInput('');
  };

  const removeCategory = (tag) => setCategories(prev => prev.filter(c => c !== tag));

  const handleSave = async () => {
    if (!name.trim()) { setSaveMsg('Name is required.'); return; }
    setSaving(true);
    setSaveMsg('');
    try {
      const data = {
        name: name.trim(),
        description: description.trim(),
        categories,
        content,
        status: 'published',
        updated_at: Timestamp.fromDate(new Date())
      };
      if (isEditing) {
        await setDoc(doc(db, 'prayers', id), data, { merge: true });
      } else {
        data.created_at = Timestamp.fromDate(new Date());
        delete data.updated_at;
        await addDoc(collection(db, 'prayers'), data);
      }
      setSaveMsg('Saved!');
      if (!isEditing) navigate('/admin/prayers');
    } catch (err) {
      console.error(err);
      setSaveMsg('Error saving.');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || dataLoading) return <div style={{ minHeight: '100vh' }}><LoadingAnimation /></div>;

  return (
    <div className="stt-admin-page">
      <a href="/admin/prayers" className="stt-admin-back">← Back to Prayers</a>
      <h1 className="stt-admin-title">{isEditing ? 'Edit Prayer' : 'New Prayer'}</h1>
      <hr />

      <div className="stt-admin-card">
        <h2 style={{ fontFamily: 'var(--font-header)', fontSize: '1.1rem', marginBottom: '16px', color: 'var(--navy)' }}>Details</h2>

        <div className="mb-3">
          <label className="stt-form-label">Name *</label>
          <input
            type="text"
            className="form-control"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Hail Mary"
          />
        </div>

        <div className="mb-3">
          <label className="stt-form-label">Categories</label>
          <p className="stt-form-hint">Press Enter or comma to add a tag.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
            {categories.map(cat => (
              <span key={cat} className="stt-prayer-tag stt-prayer-tag-removable">
                {cat}
                <button
                  onClick={() => removeCategory(cat)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', marginLeft: '4px', padding: '0 2px', color: 'inherit', fontSize: '0.85rem', lineHeight: 1 }}
                  aria-label={`Remove ${cat}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <input
            type="text"
            className="form-control"
            value={categoryInput}
            onChange={e => setCategoryInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addCategory(); }
              if (e.key === 'Escape') setCategoryInput('');
            }}
            onBlur={addCategory}
            placeholder="e.g. Marian, Daily, Traditional"
          />
        </div>

        <div className="mb-3">
          <label className="stt-form-label">Description</label>
          <p className="stt-form-hint">Shown on the prayers list page. 1–2 sentences.</p>
          <textarea
            className="form-control"
            rows={2}
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
        </div>
      </div>

      <div className="stt-admin-card">
        <h2 style={{ fontFamily: 'var(--font-header)', fontSize: '1.1rem', marginBottom: '16px', color: 'var(--navy)' }}>Prayer Text</h2>
        <p className="stt-form-hint" style={{ marginBottom: '12px' }}>Use the toolbar for bold, italic, and headings.</p>
        <div className="stt-quill-wrapper">
          <ReactQuill
            theme="snow"
            value={content}
            onChange={setContent}
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            placeholder="Enter the full prayer text here..."
          />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
        <button className="btn stt-btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Update Prayer' : 'Save Prayer'}
        </button>
        {saveMsg && (
          <span className={`stt-save-message ${saveMsg === 'Saved!' ? 'success' : 'error'}`}>
            {saveMsg}
          </span>
        )}
      </div>
    </div>
  );
};

export default AdminPrayerEditor;
